import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBag } from '../context/BagContext';
import { useAuth } from '../context/AuthContext';
import { useTracking } from '../context/TrackingContext';
import { getPrimaryProductImageUrl } from '../utils/productImages';
import { getStorefrontSwatchHex } from '../utils/productColorDisplay';
import { normalizeProductPricesForDisplay } from '../utils/productPricing';
import { getProductStorefrontPath } from '../utils/productSeoUrl';
import './ProductCard.css';

function getProductPriceDisplay(product) {
  const prices = normalizeProductPricesForDisplay(product);
  const hasSale = prices.isOnSale && prices.originalPrice > prices.price;
  return {
    effectiveSale: prices.price,
    listPrice: prices.originalPrice,
    hasSale,
  };
}

const ProductCard = ({ product, variant }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { addToBag, isInBag } = useBag();
  const { isAuthenticated } = useAuth();
  const { trackVisit } = useTracking();
  const navigate = useNavigate();
  const homeCarouselTouchStartRef = useRef(null);
  const homeCarouselSkipClickRef = useRef(false);

  // ✅ FIXED: Safety check for product data
  if (!product || !product.id) {
    return null; // Don't render anything if product is invalid
  }

  const handleAddToBag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToBag(product);
    
    // Dispatch custom event to notify Header component to refresh cart (for authenticated users)
    if (isAuthenticated) {
      window.dispatchEvent(new CustomEvent('itemAddedToCart'));
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    }
  };

  const handleProductClick = () => {
    // Track visit when clicking on product
    trackVisit(product);
  };

  const handleImageHover = () => {
    if (product.images && product.images.length > 1) {
      setCurrentImageIndex(1);
    }
  };

  const handleImageLeave = () => {
    setCurrentImageIndex(0);
  };

  const formatPrice = (price) => {
    const n = typeof price === 'number' ? price : Number(price);
    if (!Number.isFinite(n)) {
      return 'Rs. 0';
    }
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  };

  const { effectiveSale, listPrice, hasSale } = getProductPriceDisplay(product);

  // ✅ FIXED: Enhanced Cloudinary image handling with real images from backend
  const getImageUrl = () => {
    const primaryUrl = getPrimaryProductImageUrl(product);
    // Try to get image from images array first (Cloudinary objects from backend)
    if (product.images && product.images.length > 0) {
      if (currentImageIndex === 0 && primaryUrl) {
        return primaryUrl;
      }
      const image = product.images[currentImageIndex];
      // Check if image is a Cloudinary object with url property or direct string
      if (typeof image === 'object' && image.url) {
        console.log('☁️ Using Cloudinary image URL:', image.url);
        return image.url;
      } else if (typeof image === 'string') {
        console.log('📷 Using direct image URL:', image);
        return image;
      }
    }
    
    // Try to get from image property (legacy support)
    if (product.image) {
      if (typeof product.image === 'object' && product.image.url) {
        console.log('☁️ Using Cloudinary single image URL:', product.image.url);
        return product.image.url;
      } else if (typeof product.image === 'string') {
        console.log('📷 Using direct single image URL:', product.image);
        return product.image;
      }
    }
    
    // No fallback images - return null if no image available
    console.log('⚠️ No Cloudinary images available for product:', product.name);
    return null;
  };

  const currentImage = getImageUrl();

  // Debug: Log the image URL being used
  console.log(`🖼️ ProductCard for "${product.name}" - Image URL:`, currentImage);

  // ✅ FIXED: Handle image loading and errors
  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
    console.log(`✅ Image loaded successfully for "${product.name}":`, currentImage);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
    console.log('❌ Image failed to load:', currentImage, 'for product:', product.name);
    // Hide the broken image instead of showing fallback
    const imgElement = document.querySelector(`[alt="${product.name}"]`);
    if (imgElement) {
      imgElement.style.display = 'none';
    }
  };

  const homeCarousel = variant === 'homeCarousel';
  const productPath = getProductStorefrontPath(product);

  const openProduct = () => {
    if (homeCarouselSkipClickRef.current) {
      homeCarouselSkipClickRef.current = false;
      return;
    }
    handleProductClick();
    navigate(productPath);
  };

  const onHomeCarouselTouchStart = (e) => {
    const t = e.targetTouches?.[0];
    if (!t) return;
    homeCarouselTouchStartRef.current = { x: t.clientX, y: t.clientY };
    homeCarouselSkipClickRef.current = false;
  };

  const onHomeCarouselTouchEnd = (e) => {
    const start = homeCarouselTouchStartRef.current;
    homeCarouselTouchStartRef.current = null;
    const t = e.changedTouches?.[0];
    if (!start || !t) return;
    /* Ignore tap-after-drag only; never preventDefault — page scroll must stay free */
    if (Math.hypot(t.clientX - start.x, t.clientY - start.y) > 14) {
      homeCarouselSkipClickRef.current = true;
    }
  };

  const productSurface = (
    <>
        <div className="product-image-container">
          {!imageError ? (
          <img
            src={currentImage}
            alt={product.name}
            className={`product-image ${imageLoaded ? 'loaded' : ''}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            onMouseEnter={handleImageHover}
            onMouseLeave={handleImageLeave}
          />
          ) : (
            <div className="product-image-placeholder">
              <div className="placeholder-content">
                <span className="placeholder-icon">🛍️</span>
                <span className="placeholder-text">{product.name}</span>
              </div>
            </div>
          )}
          
          {/* Liked Items Action moved to content area */}

          {/* Quick Actions */}
          <div className="product-add-actions">
            <button
              className={`add-to-bag-btn ${isInBag(product.id) ? 'in-bag' : ''}`}
              onClick={handleAddToBag}
              title={isInBag(product.id) ? 'Already in bag' : 'Add to bag'}
            >
              <span className="bag-icon">🛍️</span>
              <span className="action-text">
                {isInBag(product.id) ? 'In Bag' : 'Add to Bag'}
              </span>
            </button>
          </div>
        </div>

        <div className="product-details">
          {/* Title and Price Row */}
          <div className="product-title-price-row">
            {/* 1. Product Name */}
          <h3 className="product-name">{product.name || 'Product Name'}</h3>

            {/* 2. Price — original (MRP) on top, sale price below when discounted */}
          <div className={`product-price${hasSale ? ' product-price--on-sale' : ''}`}>
            {hasSale ? (
              <>
                <span className="original-price">{formatPrice(listPrice)}</span>
                <span className="current-price">{formatPrice(effectiveSale)}</span>
              </>
            ) : (
              <span className="current-price">{formatPrice(effectiveSale)}</span>
            )}
            </div>
          </div>

          {/* Variant swatches: backend hex (#RRGGBB) and names map to real fill colors */}
          {product.colors && product.colors.length > 0 && (
            <div className="color-options-redesigned">
              <div className="color-dots">
                {product.colors.slice(0, 4).map((color, index) => {
                  const label =
                    typeof color === 'string'
                      ? color
                      : String(
                          color && typeof color === 'object'
                            ? color.name || color.value || color.color || ''
                            : color
                        );
                  return (
                    <div
                      key={`${label}-${index}`}
                      className="color-dot"
                      style={{ backgroundColor: getStorefrontSwatchHex(color) }}
                      title={label}
                    />
                  );
                })}
                {product.colors.length > 4 && (
                  <span className="color-more">+{product.colors.length - 4}</span>
                )}
              </div>
            </div>
          )}

          {/* Additional product info - Hidden */}
          <div className="product-meta">
            {product.pieces && (
              <span className="pieces-info">{product.pieces} Piece</span>
            )}
            {product.fabric && (
              <span className="fabric-info">{product.fabric}</span>
            )}
          </div>
        </div>
    </>
  );

  return (
    <div
      className={`product-card card${homeCarousel ? ' product-card--home-carousel' : ''}`}
    >
      {homeCarousel ? (
        <div
          className="product-link product-link--home-carousel-surface"
          role="link"
          tabIndex={0}
          onTouchStart={onHomeCarouselTouchStart}
          onTouchEnd={onHomeCarouselTouchEnd}
          onTouchCancel={() => {
            homeCarouselTouchStartRef.current = null;
          }}
          onClick={openProduct}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openProduct();
            }
          }}
        >
          {productSurface}
        </div>
      ) : (
        <Link to={productPath} className="product-link" onClick={handleProductClick}>
          {productSurface}
        </Link>
      )}
    </div>
  );
};

export default ProductCard; 